import type { I18nConfig } from "@memorang/constants/config/schema";
import {
	DEVICE_LOCALE_FALLBACK_RULES,
	type DeviceLocaleFallbackRule,
	LANGUAGE_NAMES,
	LOCALES,
	type Locale,
	i18nResources,
} from "@memorang/constants/i18n";
import i18n, { type i18n as I18nInstance } from "i18next";
import merge from "lodash-es/merge";
import { initReactI18next } from "react-i18next";

export const buildEffectiveI18nResources = (
	overrides: I18nConfig["overrides"],
) =>
	Object.fromEntries(
		LOCALES.map((locale) => [
			locale,
			{
				translation: merge(
					{},
					i18nResources[locale].translation,
					overrides[locale],
				),
			},
		]),
	);

const normalizeLocaleCode = (code: string) => code.trim().toLowerCase();

const getLanguageCode = (code: string) =>
	normalizeLocaleCode(code).split("-")[0];

const matchesFallbackRule = (
	rule: DeviceLocaleFallbackRule,
	locale: string,
	deviceLocale: string,
) => {
	const normalizedDeviceLocale = normalizeLocaleCode(deviceLocale);

	if ("include" in rule) {
		return rule.include.some(
			(code) => normalizeLocaleCode(code) === normalizedDeviceLocale,
		);
	}

	return (
		getLanguageCode(locale) === getLanguageCode(deviceLocale) &&
		!rule.exclude.some(
			(code) => normalizeLocaleCode(code) === normalizedDeviceLocale,
		)
	);
};

/**
 * Maps a device locale to a supported locale: an exact (case-insensitive)
 * match wins, otherwise the first supported locale whose fallback rule
 * matches applies.
 */
export const normalizeDeviceLocale = (
	deviceLocale: string,
	supportedLocales: readonly string[],
	fallbackRules: Partial<
		Record<string, DeviceLocaleFallbackRule>
	> = DEVICE_LOCALE_FALLBACK_RULES,
): string | undefined => {
	const normalizedDeviceLocale = normalizeLocaleCode(deviceLocale);

	const exactMatch = supportedLocales.find(
		(locale) => normalizeLocaleCode(locale) === normalizedDeviceLocale,
	);

	if (exactMatch) {
		return exactMatch;
	}

	return supportedLocales.find((locale) => {
		const rule = fallbackRules[locale];

		return (
			rule !== undefined && matchesFallbackRule(rule, locale, deviceLocale)
		);
	});
};

export const resolveLocale = (
	config: I18nConfig,
	persistedLocale?: string,
	deviceLocale?: string,
) => {
	if (persistedLocale && config.supportedLocales.includes(persistedLocale)) {
		return persistedLocale;
	}

	if (config.defaultToDeviceLocale && deviceLocale) {
		const supportedDeviceLocale = normalizeDeviceLocale(
			deviceLocale,
			config.supportedLocales,
		);

		if (supportedDeviceLocale) {
			return supportedDeviceLocale;
		}
	}

	return config.fallbackLocale;
};

export const applyI18nConfig = (instance: I18nInstance, config: I18nConfig) => {
	const resources = buildEffectiveI18nResources(config.overrides);

	for (const [locale, resource] of Object.entries(resources)) {
		instance.removeResourceBundle(locale, "translation");
		instance.addResourceBundle(locale, "translation", resource.translation);
	}

	instance.options.fallbackLng =
		config.fallbackLocale === "en-US"
			? ["en-US"]
			: [config.fallbackLocale, "en-US"];
};

export const initI18n = (options?: {
	locale?: Locale;
	fallbackLocale?: Locale;
}) => {
	if (i18n.isInitializing || i18n.isInitialized) {
		return i18n;
	}

	i18n.use(initReactI18next).init({
		resources: buildEffectiveI18nResources({}),
		lng: options?.locale,
		fallbackLng: options?.fallbackLocale ?? "en-US",
		interpolation: {
			escapeValue: false,
		},
		react: {
			bindI18nStore: "added removed",
		},
	});

	return i18n;
};

export const getCurrentLocale = () => i18n.language as Locale;
export const getCurrentLanguageName = () => LANGUAGE_NAMES[getCurrentLocale()];

export const formatList = (
	locale: Intl.LocalesArgument | undefined,
	values: string[],
	options?: Intl.ListFormatOptions,
) => {
	if (typeof Intl.ListFormat !== "function") {
		return values.join(", ");
	}

	return new Intl.ListFormat(locale ?? i18n.language, {
		type: "conjunction",
		style: "long",
		...options,
	}).format(values);
};
